(() => {
  'use strict';

  const REVEAL_SELECTOR = '[data-reveal], [data-reveal-item]';
  const READY_CLASS = 'reveal-ready';
  const PENDING_CLASS = 'is-reveal-pending';
  const REFRESH_EVENT = 'theme:reveal:refresh';

  class ThemeRevealController {
    constructor() {
      this.observer = null;
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleIntersect = this.handleIntersect.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleRefresh = this.handleRefresh.bind(this);
    }

    get animationsEnabled() {
      return document.body?.dataset.enableThemeAnimations === 'true';
    }

    get reduceMotion() {
      return this.reduceMotionQuery.matches;
    }

    init() {
      document.documentElement.classList.add(READY_CLASS);

      if (!this.animationsEnabled || this.reduceMotion) {
        this.revealAll(document);
      } else {
        this.setupObserver();
        this.observeIn(document);
      }

      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      document.addEventListener('shopify:section:reorder', this.handleSectionLoad);
      document.addEventListener(REFRESH_EVENT, this.handleRefresh);
    }

    setupObserver() {
      if (this.observer) return;
      this.observer = new IntersectionObserver(this.handleIntersect, {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12
      });
    }

    getTargets(scope) {
      if (!scope) return [];

      const targets = new Set();

      if (scope instanceof Element && scope.matches(REVEAL_SELECTOR)) {
        targets.add(scope);
      }

      scope.querySelectorAll?.(REVEAL_SELECTOR).forEach((node) => {
        if (node.classList.contains('is-revealed')) return;
        targets.add(node);
      });

      return Array.from(targets);
    }

    isScrollRevealDisabled(node) {
      if (!(node instanceof Element)) return false;
      return Boolean(node.closest('[data-reveal-disabled="true"]'));
    }

    markRevealed(node) {
      if (!(node instanceof Element)) return;
      node.classList.remove(PENDING_CLASS);
      node.classList.add('is-revealed');
      this.observer?.unobserve(node);
    }

    revealAll(scope) {
      this.getTargets(scope).forEach((node) => this.markRevealed(node));
    }

    observeIn(scope) {
      if (!this.observer) return;

      this.getTargets(scope).forEach((node) => {
        if (this.isScrollRevealDisabled(node)) {
          this.markRevealed(node);
          return;
        }

        if (node.dataset.revealImmediate === 'true') {
          this.markRevealed(node);
          return;
        }

        if (!node.classList.contains('is-revealed')) {
          node.classList.add(PENDING_CLASS);
        }
        this.observer.observe(node);
      });
    }

    unobserveIn(scope) {
      if (!this.observer || !scope) return;
      this.getTargets(scope).forEach((node) => this.observer.unobserve(node));
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;
        this.markRevealed(entry.target);
      });
    }

    handleSectionLoad(event) {
      const sectionRoot = event.target?.closest?.('.shopify-section') || event.target;
      if (!sectionRoot) return;

      if (!this.animationsEnabled || this.reduceMotion) {
        this.revealAll(sectionRoot);
        return;
      }

      this.observeIn(sectionRoot);
    }

    handleSectionUnload(event) {
      const sectionRoot = event.target?.closest?.('.shopify-section') || event.target;
      if (!sectionRoot) return;
      this.unobserveIn(sectionRoot);
    }

    handleRefresh(event) {
      const scope = event?.detail?.scope;
      if (scope && !(scope instanceof Element) && scope !== document) return;

      const targetScope = scope || document;

      if (!this.animationsEnabled || this.reduceMotion) {
        this.revealAll(targetScope);
        return;
      }

      this.observeIn(targetScope);
    }
  }

  const controller = new ThemeRevealController();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => controller.init(), { once: true });
  } else {
    controller.init();
  }
})();
